'use client'

import { ChevronsRight } from 'lucide-react'
import type { ReactNode } from 'react'

import { HeaderToolbarSeparator } from '@/components/navigation/header-toolbar-separator'
import { Button } from '@/components/ui/button'
import { SheetClose, SheetTitle } from '@/components/ui/sheet'
import { artifactCanvasSectionPaddingClass } from '@/lib/artifact-canvas-section-padding'
import { cn } from '@/lib/utils'

export type ArtifactCanvasHeaderProps = {
  title: ReactNode
  /** Optional control beside the title (e.g. pin). */
  titleAddon?: ReactNode
  /** Optional trailing controls (e.g. Preview | Markdown switcher). */
  trailing?: ReactNode
  className?: string
}

/**
 * Canonical artifact pane header:
 * [ChevronsRight close] | [Title] … [trailing?]
 * Toolbar rhythm matches `PageHeaderLeading` (gap-0.5 cluster + `HeaderToolbarSeparator`).
 */
export function ArtifactCanvasHeader({
  title,
  titleAddon,
  trailing,
  className,
}: ArtifactCanvasHeaderProps) {
  return (
    <header
      className={cn(
        'flex shrink-0 items-center gap-2',
        artifactCanvasSectionPaddingClass,
        className,
      )}
    >
      <div className="flex shrink-0 items-center gap-0.5">
        <SheetClose asChild>
          <Button
            type="button"
            variant="ghostToolbar"
            size="iconSm"
            aria-label="Close"
            title="Close"
          >
            <ChevronsRight className="size-4" aria-hidden />
          </Button>
        </SheetClose>

        <HeaderToolbarSeparator />
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-0.5">
        <SheetTitle className="min-w-0 truncate text-base font-semibold leading-none">
          {title}
        </SheetTitle>
        {titleAddon ? <div className="shrink-0">{titleAddon}</div> : null}
      </div>

      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </header>
  )
}
